/**
 * Shared utilities for the FTP Editor.
 * Ported from ftp-editor.js and code-sync.js helpers.
 */

export function stripScriptsAndLinks(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<script[^>]*\/>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/\s+on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+on\w+\s*=\s*'[^']*'/gi, '')
    .replace(/href\s*=\s*"javascript:[^"]*"/gi, 'href="#"')
    .replace(/href\s*=\s*'javascript:[^']*'/gi, "href='#'");
}

export function stripNodeIds(html: string): string {
  return html
    .replace(/\s+data-ftp-node-id="[^"]*"/g, '')
    .replace(/\s+data-ftp-editor-pad="[^"]*"/g, '')
    .replace(/\s+data-ftp-editor-pad-b="[^"]*"/g, '')
    .replace(/\s+data-ftp-real-src="[^"]*"/g, '')
    .replace(/\s+data-ftp-placeholder="[^"]*"/g, '')
    .replace(/\s+data-ftp-width="[^"]*"/g, '')
    .replace(/\s+data-ftp-editor-mode="html"/g, '')
    .replace(/\s+class="[^"]*ftp-grab-zone-(?:top|bottom)[^"]*"/g, (match) => {
      const cleaned = match.replace(/ftp-grab-zone-(?:top|bottom)/g, '').replace(/\s+/g, ' ').trim();
      return cleaned === 'class=""' ? '' : cleaned;
    })
    .replace(/\s+class="ftp-img-broken"/g, '')
    .replace(/\s+class=""/g, '')
    .replace(/<style\s+id="ftp-editor-styles">[\s\S]*?<\/style>/g, '')
    .replace(/<style\s+id="ftp-unlock-highlight-styles">[\s\S]*?<\/style>/g, '');
}

export function sanitizeHead(headInner: string): string {
  let h = headInner;
  h = h.replace(/<script[\s\S]*?<\/script>/gi, '');
  h = h.replace(/<script[^>]*\/>/gi, '');
  h = h.replace(/<link[^>]*>/gi, '');
  h = h.replace(/<title[\s\S]*?<\/title>/gi, '');
  h = h.replace(/<meta[^>]*>/gi, '');
  return h.trim();
}

export function sanitizeBody(bodyInner: string): string {
  let b = bodyInner;
  b = b.replace(/<script[\s\S]*?<\/script>/gi, '');
  b = b.replace(/<script[^>]*\/>/gi, '');
  b = b.replace(/<link[^>]*>/gi, '');
  return b;
}

export function htmlToPlainText(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  const scripts = tmp.querySelectorAll('script, style, head');
  scripts.forEach((el) => el.remove());

  function walkNode(node: Node): void {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const tag = el.tagName;

        if (tag === 'BR') {
          const text = document.createTextNode('\n');
          node.replaceChild(text, child);
          continue;
        }

        walkNode(el);

        if (tag === 'A') {
          const href = el.getAttribute('href');
          const text = el.textContent || '';
          if (href && href !== '#' && href !== text) {
            el.textContent = `${text} (${href})`;
          }
        }

        if (tag === 'LI') {
          el.textContent = '- ' + (el.textContent || '');
        }

        if (/^H[1-6]$/.test(tag)) {
          el.textContent = (el.textContent || '').toUpperCase();
        }

        const blockTags = [
          'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
          'TR', 'LI', 'BLOCKQUOTE', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER',
        ];

        if (blockTags.includes(tag)) {
          el.appendChild(document.createTextNode('\n'));
          if (['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'].includes(tag)) {
            el.appendChild(document.createTextNode('\n'));
          }
        }

        if (tag === 'TD' || tag === 'TH') {
          el.appendChild(document.createTextNode(' '));
        }
      }
    }
  }

  walkNode(tmp);

  let text = tmp.textContent || tmp.innerText || '';
  text = text.replace(/[ \t]+/g, ' ');
  text = text.replace(/^ +/gm, '').replace(/ +$/gm, '');
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

export interface ExtractedBody {
  body: string;
  head: string;
  bodyAttrs: string;
  headCss: string;
}

export function extractBody(fullHtml: string): ExtractedBody {
  if (!fullHtml) return { body: '', head: '', bodyAttrs: '', headCss: '' };

  const headMatch = fullHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  let headInner = headMatch ? headMatch[1] : '';

  const headStyles: string[] = [];
  headInner.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_, css: string) => {
    if (css.trim()) headStyles.push(css.trim());
    return '';
  });
  headInner = headInner.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  headInner = sanitizeHead(headInner);

  const bodyMatch = fullHtml.match(/<body([^>]*)>([\s\S]*?)<\/body>/i);
  const bodyAttrs = bodyMatch ? bodyMatch[1] : '';
  let bodyInner = bodyMatch ? bodyMatch[2] : fullHtml;
  bodyInner = sanitizeBody(bodyInner);

  bodyInner.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_, css: string) => {
    if (css.trim()) headStyles.push(css.trim());
    return '';
  });
  bodyInner = bodyInner.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

  return { body: bodyInner, head: headInner, bodyAttrs, headCss: headStyles.join('\n\n') };
}

export function reconstructFullHtml(bodyHtml: string, css: string, headContent: string, bodyAttrs: string): string {
  const headBlock = headContent || '<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Email</title>';
  const styleBlock = css && css.trim() ? `\n<style>\n${css.trim()}\n</style>\n` : '';
  const attrs = bodyAttrs || '';
  return `<!DOCTYPE html><html><head>${headBlock}${styleBlock}</head><body${attrs}>${bodyHtml}</body></html>`;
}

export function looksLikeFullDocument(text: string): boolean {
  const trimmed = text.trim();
  return /^<!DOCTYPE/i.test(trimmed) || /^<html/i.test(trimmed) || /<head/i.test(trimmed) || /<body/i.test(trimmed);
}

const EMAIL_FONTS = [
  'Arial, Helvetica, sans-serif',
  'Georgia, serif',
  '"Times New Roman", Times, serif',
  '"Courier New", Courier, monospace',
  'Verdana, Geneva, sans-serif',
  'Tahoma, Geneva, sans-serif',
  '"Trebuchet MS", Helvetica, sans-serif',
  '"Lucida Console", Monaco, monospace',
];

export { EMAIL_FONTS };

export const SELECTABLE_TAGS = [
  'TABLE', 'TD', 'TH', 'DIV', 'SECTION', 'P', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'A', 'IMG', 'BUTTON', 'HR',
];

export const INLINE_EDITABLE_TAGS = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TD', 'TH', 'LI'];
export const LOCKABLE_TAGS = ['P', 'H1', 'H2', 'H3', 'IMG'];
export const NON_SELECTABLE_TAGS = ['HTML', 'HEAD', 'META', 'TITLE', 'STYLE', 'SCRIPT', 'LINK'];

export const DEFAULT_LINK_COLOR = '#1188E6';

export const SAMPLE_VARIABLES: Record<string, string> = {
  '{{first_name}}': 'John',
  '{{last_name}}': 'Doe',
  '{{facility_name}}': 'Sample Practice',
  '{{facility_address}}': '123 Main St',
  '{{facility_city}}': 'Anytown',
  '{{facility_state}}': 'CA',
  '{{facility_zip}}': '90210',
  '{{facility_country}}': 'United States',
  '{{facility_phone}}': '(555) 123-4567',
  '{{facility_email}}': 'info@example.com',
  '{{unsubscribe_link}}': '#unsubscribe',
  '{{weblink}}': '#webversion',
};

/**
 * Token → human description used by the "Insert Variable" picker across editors
 * (FtpEditor and the DnD editor). Shared so both stay in sync.
 */
export const DEFAULT_EDITOR_VARIABLES: Record<string, string> = {
  '{{first_name}}': 'Recipient first name',
  '{{last_name}}': 'Recipient last name',
  '{{facility_id}}': 'Facility ID',
  '{{facility_name}}': 'Facility name',
  '{{facility_address}}': 'Facility address',
  '{{facility_city}}': 'Facility city',
  '{{facility_state}}': 'Facility state',
  '{{facility_zip}}': 'Facility zip / postal code',
  '{{facility_country}}': 'Facility country',
  '{{facility_phone}}': 'Facility phone number',
  '{{facility_email}}': 'Facility email address',
  '{{facility_website}}': 'Facility website URL',
  '{{facility_facebook}}': 'Facility Facebook URL',
  '{{facility_twitter}}': 'Facility Twitter / X URL',
  '{{facility_instagram}}': 'Facility Instagram URL',
  '{{facility_pinterest}}': 'Facility Pinterest URL',
  '{{facility_linkedin}}': 'Facility LinkedIn URL',
  '{{facility_tiktok}}': 'Facility TikTok URL',
  '{{facility_youtube}}': 'Facility YouTube URL',
  '{{weblink}}': 'View email in browser link',
  '{{unsubscribe_link}}': 'Unsubscribe link',
};

export function substituteVariables(html: string, variables?: Record<string, string>): string {
  let result = html;
  for (const [key, value] of Object.entries(SAMPLE_VARIABLES)) {
    result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
  }
  if (variables) {
    for (const key of Object.keys(variables)) {
      const escaped = key.replace(/[{}]/g, '\\$&');
      if (!result.includes(key)) continue;
      result = result.replace(new RegExp(escaped, 'g'), key);
    }
  }
  return result;
}

export function getGlobalLinkColor(iframeDoc: Document | null): string {
  if (!iframeDoc) return DEFAULT_LINK_COLOR;
  const styleTag = iframeDoc.getElementById('ftp-body-global-styles');
  if (styleTag) {
    const match = styleTag.textContent?.match(/color:\s*([^;}]+)/);
    if (match) return match[1].trim();
  }
  return DEFAULT_LINK_COLOR;
}

/**
 * Determines whether a DOM element is visually empty — no meaningful content
 * that would render in an email client. Preserves intentional spacers, dividers,
 * and background images. MSO conditional comments alone do NOT prevent removal
 * since they only target Outlook and produce no visible output on their own.
 */
export function isElementVisuallyEmpty(el: Element): boolean {
  if (el.querySelector('img, video, iframe, svg, hr')) return false;

  const text = el.textContent?.replace(/\u00A0/g, '').trim() ?? '';
  if (text.length > 0) return false;

  let hasNonMsoContent = false;
  for (let i = 0; i < el.childNodes.length; i++) {
    const node = el.childNodes[i];
    if (node.nodeType === Node.COMMENT_NODE) continue;
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent?.replace(/\u00A0/g, '').trim()) { hasNonMsoContent = true; break; }
      continue;
    }
    if (node.nodeType === Node.ELEMENT_NODE) { hasNonMsoContent = true; break; }
  }
  if (!hasNonMsoContent) return true;

  const style = (el as HTMLElement).style;
  if (!style) return true;

  if (style.backgroundImage && style.backgroundImage !== 'none') return false;

  if (style.height && style.height !== '0' && style.height !== '0px' && style.height !== 'auto') return false;
  if (style.minHeight && style.minHeight !== '0' && style.minHeight !== '0px') return false;

  if (style.borderTop && !style.borderTop.startsWith('0') && style.borderTop !== 'none') return false;
  if (style.borderBottom && !style.borderBottom.startsWith('0') && style.borderBottom !== 'none') return false;
  if (style.border && !style.border.startsWith('0') && style.border !== 'none') return false;

  return true;
}

/**
 * After removing an element, walk up the DOM and remove ancestor containers
 * that are now visually empty. Stops at BODY/HTML or any element that still
 * has visible content.
 */
export function pruneEmptyAncestors(el: Element | null): void {
  while (el && el.tagName !== 'BODY' && el.tagName !== 'HTML') {
    const parent = el.parentElement;
    if (!isElementVisuallyEmpty(el)) break;
    el.remove();
    el = parent;
  }
}
